import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowUp,
  CalendarDays,
  Check,
  Copy,
  FileText,
  MessageSquare,
  Mic,
  MoreVertical,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  Scale,
  Search,
  Square,
  SquarePen,
  Trash2,
  Volume2,
  X,
} from "lucide-react";
import Navbar from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import juriApi, { JURI_API_BASE, getAuthHeader } from "../services/juriApi";
import "./JuriAssistance.css";

const LANGUAGES = [
  { name: "English", code: "en" },
  { name: "Tamil (தமிழ்)", code: "ta" },
  { name: "Hindi (हिंदी)", code: "hi" },
  { name: "Malayalam (മലയാളം)", code: "ml" },
  { name: "Telugu (తెలుగు)", code: "te" },
  { name: "Bengali (বাংলা)", code: "bn" },
  { name: "Marathi (मराठी)", code: "mr" },
  { name: "Gujarati (ગુજરાતી)", code: "gu" },
  { name: "Kannada (ಕನ್ನಡ)", code: "kn" },
  { name: "Punjabi (ਪੰਜਾਬੀ)", code: "pa" },
  { name: "Urdu (اردو)", code: "ur" },
  { name: "Odia (ଓଡ଼ିଆ)", code: "or" },
  { name: "Assamese (অসমীয়া)", code: "as" },
  { name: "Kashmiri (کأشُر)", code: "ks" },
  { name: "Nepali (नेपाली)", code: "ne" },
  { name: "Konkani (कोंकणी)", code: "kok" },
  { name: "Sindhi (سنڌي)", code: "sd" },
  { name: "Manipuri (মৈতৈলোন)", code: "mni" }
];

const MAX_CASE_LINKS = 10;

// Suggestion tiles shown in the centred empty state (no messages yet). Same
// four actions as the quick-prompt row above the composer.
const EMPTY_SUGGESTIONS = [
  { key: "issues", title: "Identify legal issues", sub: "Spot key legal matters in your case", Icon: Scale, bg: "rgba(99, 102, 241, 0.14)", fg: "#6366f1" },
  { key: "related", title: "Show related cases", sub: "Find similar cases and precedents", Icon: Search, bg: "rgba(16, 185, 129, 0.14)", fg: "#10b981" },
  { key: "brief", title: "Prepare hearing brief", sub: "Generate a brief for upcoming hearings", Icon: FileText, bg: "rgba(245, 158, 11, 0.16)", fg: "#f59e0b" },
  { key: "hearings", title: "List of hearings", sub: "View upcoming and past hearing dates", Icon: CalendarDays, bg: "rgba(236, 72, 153, 0.14)", fg: "#ec4899" },
];

// How many recent conversations the sidebar shows before "View all conversations".
const RECENT_CONVERSATIONS_LIMIT = 8;

export function parseStructuralCases(text) {
  let count = 0;
  let normalized = String(text).replace(/(\[\[CASE:.+?\]\])(?!\s*\n|\s*$)/g, '$1\n');

  let result = normalized.replace(/\[\[CASE:(.+?)\]\]/g, (_match, rawName) => {
    count += 1;
    if (count > MAX_CASE_LINKS) {
      return rawName.trim();
    }
    return `\u0001CASE:${rawName.trim()}\u0001`;
  });

  result = result.replace(/\[\[CASE:[^\]]*$/g, '');
  return result;
}

const SimpleMarkdown = ({ text, onExpandCase, expandedCases }) => {
  if (!text) return null;
  const lines = String(text).split("\n");
  
  const elements = [];
  let tableRows = [];
  let inTable = false;

  const flushTable = () => {
    if (tableRows.length > 0) {
      // Basic markdown table parsing
      const headerLine = tableRows[0];
      // Filter out empty strings from splitting on the outer pipe edges
      const parseCells = (rowStr) => rowStr.split("|").map(c => c.trim()).slice(1, -1);
      
      const headers = parseCells(headerLine);
      const bodyLines = tableRows.length > 2 ? tableRows.slice(2) : []; // skip header & divider
      
      elements.push(
        <div className="juri-table-wrapper" key={`table-${elements.length}`} style={{ overflowX: "auto", margin: "16px 0" }}>
          <table className="juri-markdown-table" style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
            <thead style={{ backgroundColor: "rgba(255,255,255,0.05)" }}>
              <tr>
                {headers.map((h, i) => <th key={i} style={{ padding: "12px", border: "1px solid var(--juri-border)" }}>{renderInline(h, onExpandCase, expandedCases)}</th>)}
              </tr>
            </thead>
            <tbody>
              {bodyLines.map((row, rIdx) => {
                const cells = parseCells(row);
                return (
                  <tr key={rIdx}>
                    {cells.map((c, cIdx) => <td key={cIdx} style={{ padding: "12px", border: "1px solid var(--juri-border)" }}>{renderInline(c, onExpandCase, expandedCases)}</td>)}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
      tableRows = [];
      inTable = false;
    }
  };

  lines.forEach((line, i) => {
    const trimmed = line.trim();
    if (trimmed.startsWith("|") && trimmed.endsWith("|")) {
      inTable = true;
      tableRows.push(trimmed);
      return;
    } else if (inTable) {
      flushTable();
    }

    if (!inTable) {
      if (line.startsWith("###### ")) { elements.push(<h6 key={i}>{line.slice(7)}</h6>); return; }
      if (line.startsWith("##### ")) { elements.push(<h5 key={i}>{line.slice(6)}</h5>); return; }
      if (line.startsWith("#### ")) { elements.push(<h4 key={i}>{line.slice(5)}</h4>); return; }
      if (line.startsWith("### ")) { elements.push(<h3 key={i}>{line.slice(4)}</h3>); return; }
      if (line.startsWith("## ")) { elements.push(<h2 key={i}>{line.slice(3)}</h2>); return; }
      if (line.startsWith("# ")) { elements.push(<h1 key={i}>{line.slice(2)}</h1>); return; }
      if (line.startsWith("> ")) { elements.push(<blockquote key={i} className="juri-blockquote">{renderInline(line.slice(2), onExpandCase, expandedCases)}</blockquote>); return; }
      if (line.startsWith("- ") || line.startsWith("* ")) { elements.push(<li key={i}>{renderInline(line.slice(2), onExpandCase, expandedCases)}</li>); return; }
      if (trimmed === "---") { elements.push(<hr key={i} />); return; }
      if (!trimmed) { elements.push(<br key={i} />); return; }
      elements.push(<p key={i}>{renderInline(line, onExpandCase, expandedCases)}</p>);
    }
  });

  flushTable(); // flush any remaining table at the end

  return <>{elements}</>;
};

const renderInline = (line, onExpandCase, expandedCases) => {
  const parts = line.split(/(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\)|\u0001CASE:.+?\u0001)/g);
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) {
      return <strong key={i}>{p.slice(2, -2)}</strong>;
    }
    if (p.startsWith("\u0001CASE:") && p.endsWith("\u0001")) {
      const caseName = p.slice(6, -1);
      const isExpanded = expandedCases ? expandedCases.has(caseName) : false;
      return (
        <button
          key={i}
          type="button"
          aria-label={`Explain ${caseName} in detail`}
          disabled={isExpanded}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (onExpandCase) onExpandCase(caseName);
          }}
          className="juri-case-chip"
          data-expanded={isExpanded}
          style={{
            background: isExpanded ? "var(--bg-lighter, #eee)" : "none",
            border: "none",
            color: isExpanded ? "var(--text-muted, #888)" : "var(--juri-brand)",
            cursor: isExpanded ? "not-allowed" : "pointer",
            textDecoration: isExpanded ? "none" : "underline",
            textDecorationStyle: "dotted",
            textUnderlineOffset: "4px",
            padding: 0,
            font: "inherit",
            display: "inline"
          }}
        >
          🔗 {caseName}
        </button>
      );
    }
    const linkMatch = p.match(/^\[([^\]]+)\]\([^)]+\)$/);
    if (linkMatch) return <span key={i}>{linkMatch[1]}</span>;
    return p;
  });
};

const Section = ({ title, content, highlight = false, onExpandCase, expandedCases }) => {
  if (!content) return null;

  const renderNode = (node) => {
    if (Array.isArray(node)) {
      return (
        <ul className="juri-res-list">
          {node.map((item, i) => (
            <li key={i}>{typeof item === "object" ? renderNode(item) : String(item)}</li>
          ))}
        </ul>
      );
    } else if (typeof node === "object" && node !== null) {
      return (
        <div className="juri-res-nested">
          {Object.entries(node).map(([key, val], i) => (
            <div key={i} className="juri-res-pair">
              <span className="juri-res-key">{key.replace(/_/g, " ").toUpperCase()}:</span>
              <span className="juri-res-val">
                {typeof val === "object" ? renderNode(val) : String(val)}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return <SimpleMarkdown text={String(node)} onExpandCase={onExpandCase} expandedCases={expandedCases} />;
  };

  return (
    <div className={`juri-res-section ${highlight ? "highlight" : ""}`}>
      <h4>{title}</h4>
      <div className="juri-res-content">{renderNode(content)}</div>
    </div>
  );
};

const StructuredResponse = ({ s, onExpandCase, expandedCases }) => {
  if (!s) return null;
  return (
    <div className="juri-structured-container">
      <Section title="Facts Understood" content={s.facts_understood} onExpandCase={onExpandCase} expandedCases={expandedCases} />
      <Section title="People Involved" content={s.people_involved} onExpandCase={onExpandCase} expandedCases={expandedCases} />
      <Section title="Issues Identified" content={s.issues_identified} onExpandCase={onExpandCase} expandedCases={expandedCases} />
      <Section title="Relevant Law" content={s.relevant_law} onExpandCase={onExpandCase} expandedCases={expandedCases} />

      {s.relevant_precedents && s.relevant_precedents.length > 0 && (
        <div className="juri-res-section">
          <h4>Relevant Precedents</h4>
          <div className="juri-precedents-grid">
            {s.relevant_precedents.map((p, i) => (
              <div key={i} className="juri-precedent-pill">
                <strong>{p.title}</strong>
                <div className="juri-prec-meta">
                  {p.court && <span>{p.court}</span>}
                  {p.date && <span> • {p.date}</span>}
                </div>
                {p.principle && <p className="juri-prec-principle">{p.principle}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      <Section title="Application" content={s.application} highlight onExpandCase={onExpandCase} expandedCases={expandedCases} />
      <Section title="Assumptions & Missing Facts" content={s.assumptions_and_missing_facts} onExpandCase={onExpandCase} expandedCases={expandedCases} />
      <Section title="Suggested Next Steps" content={s.suggested_next_steps} onExpandCase={onExpandCase} expandedCases={expandedCases} />
    </div>
  );
};

// A response is rendered as rich structured sections only for legal-analysis
// intents. Plain chat / summaries (is_chat) keep the natural markdown narrative.
const hasStructuredAnalysis = (structured) =>
  Boolean(structured) &&
  !structured.is_chat &&
  Boolean(
    structured.facts_understood ||
      structured.issues_identified ||
      structured.relevant_law ||
      (structured.relevant_precedents && structured.relevant_precedents.length) ||
      structured.suggested_next_steps
  );

const isPdfFile = (file) => {
  const name = file?.name || file?.filename || "";
  const type = file?.type || file?.fileType || file?.file_type || "";
  return type.toLowerCase().includes("pdf") || name.toLowerCase().endsWith(".pdf");
};

const resolveDocumentUrl = (viewUrl) => {
  if (!viewUrl) return "";
  if (viewUrl.startsWith("blob:") || viewUrl.startsWith("http")) return viewUrl;
  return `${JURI_API_BASE}${viewUrl}`;
};

const displayTextWithoutAttachmentLinks = (message) => {
  if (!message.attachments?.length) return message.text;
  return String(message.text || "")
    .replace(/\n\nAttached documents:\n(?:- \[[^\]]+\]\([^)]+\)\n?)+/g, "")
    .trim();
};

const DocumentAttachmentCard = ({ attachment }) => {
  const url = resolveDocumentUrl(attachment.viewUrl);
  const type = attachment.fileType || attachment.file_type || attachment.filename?.split(".").pop() || "document";
  const analysisJson = attachment.analysisJson || attachment.analysis_json;

  return (
    <div className="juri-document-card">
      <div className="juri-document-card-header">
        <div className="juri-document-meta">
          <div className="juri-document-name">{attachment.filename || "Document"}</div>
          <div className="juri-document-type">{String(type).toUpperCase()}</div>
        </div>
        {url && (
          <a href={url} target="_blank" rel="noreferrer" className="juri-document-open-link">
            Open
          </a>
        )}
      </div>
      {isPdfFile(attachment) && url ? (
        <iframe className="juri-document-preview" src={url} title={`Preview of ${attachment.filename}`} />
      ) : (
        <div className="juri-document-fallback">
          Word preview opens in a separate tab. PDF files can be scrolled here.
        </div>
      )}
      {analysisJson && Object.keys(analysisJson).length > 0 && (
        <details className="juri-document-analysis">
          <summary>Extracted analysis</summary>
          <Section title="Document Findings" content={analysisJson} />
        </details>
      )}
    </div>
  );
};

const SourceList = ({ citations }) => {
  if (!Array.isArray(citations) || citations.length === 0) return null;

  return (
    <details className="juri-source-list">
      <summary>Sources used ({citations.length})</summary>
      <div className="juri-source-items">
        {citations.map((source, idx) => (
          <div key={`${source.ref_id || source.id || source.title || "source"}-${idx}`} className="juri-source-item">
            <div className="juri-source-title">
              {source.title || source.source_label || source.ref_id || "Legal source"}
            </div>
            <div className="juri-source-meta">
              {[
                source.court,
                source.court_name,
                source.metadata?.court,
                source.metadata?.court_name,
                source.type,
                source.document_type,
                source.date,
              ]
                .filter(Boolean)
                .join(" - ")}
            </div>
            {(source.text_excerpt || source.snippet || source.principle) && (
              <p>{source.text_excerpt || source.snippet || source.principle}</p>
            )}
          </div>
        ))}
      </div>
    </details>
  );
};

const shouldShowActions = (structured) => Boolean(structured?.show_actions);

const mapBackendActions = (message) => {
  const structured = message.metadata?.structured;
  if (message.role !== "assistant" || !shouldShowActions(structured) || !message.actions?.length) {
    return undefined;
  }

  return message.actions.map((act) => ({
    ...act,
    onClick: act.onClick || (() => window.open(`${JURI_API_BASE}${act.url}`, "_blank")),
  }));
};

const JuriAssistance = () => {
  const { globalUser } = useAuth();
  const navigate = useNavigate();
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Empty on purpose: with no messages the chat area shows the centred
  // "Hello, I'm Jurident AI" hero + suggestion tiles instead of a greeting bubble.
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [conversations, setConversations] = useState([]);
  // Conversation sidebar starts collapsed — the chat gets the full width until
  // the user asks for their history.
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showAllConversations, setShowAllConversations] = useState(false);
  // Conversation row whose "…" menu is open (only one at a time).
  const [openConvMenuId, setOpenConvMenuId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [matterContext] = useState({ court: "", caseNumber: "" });

  // TTS state
  const [playingAudioId, setPlayingAudioId] = useState(null);
  const currentAudioRef = useRef(null);

  // Copy-to-clipboard feedback (ChatGPT-style icon row under responses)
  const [copiedId, setCopiedId] = useState(null);
  const copyMessage = async (m) => {
    try {
      await navigator.clipboard.writeText(displayTextWithoutAttachmentLinks(m));
      setCopiedId(m.id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch (e) {
      console.error("copy failed", e);
    }
  };

  // Audio recording states
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [showLanguages, setShowLanguages] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState("en");
  const [isTranscribing, setIsTranscribing] = useState(false);

  // Timeline Custom Modal state
  const [showTimelineModal, setShowTimelineModal] = useState(false);
  const [timelineColumns, setTimelineColumns] = useState("Date, Event, Party Responsible, Document");

  const handledCasesRef = useRef(new Set());
  const [expandedCases, setExpandedCases] = useState(new Set());
  const handleExpandCase = (caseName) => {
    if (handledCasesRef.current.has(caseName)) return;
    handledCasesRef.current.add(caseName);
    setExpandedCases((prev) => new Set(prev).add(caseName));
    handleSend(`Explain ${caseName} in detail.`);
  };

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);

  const playAudioOutLoud = async (messageId, text) => {
    if (playingAudioId === messageId) {
      // If already playing this message, stop it
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current = null;
      }
      setPlayingAudioId(null);
      return;
    }

    try {
      setPlayingAudioId(messageId);
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
      }

      const headers = getAuthHeader();
      const res = await fetch(`${JURI_API_BASE}/api/chat/synthesize`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ text }),
      });

      if (!res.ok) throw new Error("Failed to synthesize speech");
      
      const blob = await res.blob();
      const audioUrl = URL.createObjectURL(blob);
      const audio = new Audio(audioUrl);
      
      // Speed up the Google TTS reading pace slightly
      audio.playbackRate = 1.3;
      
      currentAudioRef.current = audio;
      
      audio.onended = () => {
        setPlayingAudioId(null);
        currentAudioRef.current = null;
      };
      
      audio.play();
    } catch (e) {
      console.error(e);
      setPlayingAudioId(null);
      alert("Failed to play audio. Please ensure GROQ API key is valid.");
    }
  };

  // Recording timer effect
  useEffect(() => {
    if (isRecording) {
      timerRef.current = setInterval(() => {
        setRecordingDuration((prev) => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
      setRecordingDuration(0);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isRecording]);

  const startRecording = async (langCode) => {
    setSelectedLanguage(langCode);
    setShowLanguages(false);
    audioChunksRef.current = [];

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      let options = {};
      if (MediaRecorder.isTypeSupported("audio/webm")) {
        options = { mimeType: "audio/webm" };
      } else if (MediaRecorder.isTypeSupported("audio/mp4")) {
        options = { mimeType: "audio/mp4" };
      }

      const mediaRecorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.start(250);
      setIsRecording(true);
    } catch (err) {
      console.error("Failed to start recording:", err);
      alert("Could not access microphone. Please check permissions.");
    }
  };

  const stopRecording = (cancel = false) => {
    const mediaRecorder = mediaRecorderRef.current;
    if (!mediaRecorder || mediaRecorder.state === "inactive") return;

    mediaRecorder.onstop = async () => {
      setIsRecording(false);

      // Clean up tracks
      const stream = mediaRecorder.stream;
      stream.getTracks().forEach((track) => track.stop());

      if (cancel) {
        audioChunksRef.current = [];
        return;
      }

      const audioBlob = new Blob(audioChunksRef.current, { type: mediaRecorder.mimeType });
      if (audioBlob.size === 0) return;

      setIsTranscribing(true);

      const formData = new FormData();
      const extension = mediaRecorder.mimeType.includes("mp4") ? "mp4" : "webm";
      formData.append("file", audioBlob, `audio.${extension}`);
      formData.append("language", selectedLanguage);

      try {
        const authHeaders = await getAuthHeader();
        const response = await fetch(`${JURI_API_BASE}/api/chat/transcribe`, {
          method: "POST",
          headers: {
            ...authHeaders,
          },
          body: formData,
        });

        if (!response.ok) {
          throw new Error("Failed to transcribe audio");
        }

        const data = await response.json();
        const transcription = data.text;

        if (transcription && transcription.trim()) {
          handleSend(transcription);
        } else {
          alert("Could not detect any speech. Please try again.");
        }
      } catch (e) {
        console.error("Transcription error:", e);
        alert("Error transcribing audio: " + e.message);
      } finally {
        setIsTranscribing(false);
      }
    };

    mediaRecorder.stop();
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  const messagesEndRef = useRef(null);

  const addMessage = (msg) => {
    const id = Date.now().toString() + Math.random().toString(36).substring(7);
    setMessages((prev) => [...prev, { ...msg, id }]);
    return id;
  };

  const updateMessage = (id, updates) => {
    setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, ...updates } : m)));
  };

  const fetchConversations = async () => {
    if (!globalUser) return;
    try {
      const res = await juriApi.get("/api/chat/conversations");
      setConversations(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error("fetchConversations failed", e);
      setConversations([]);
    }
  };

  useEffect(() => {
    fetchConversations();

  }, [globalUser]);

  // Any click outside a row's "…" popover closes it.
  useEffect(() => {
    if (!openConvMenuId) return;
    const close = () => setOpenConvMenuId(null);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [openConvMenuId]);

  useEffect(() => {
    if (messages.length > 1) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  const loadConversation = async (id) => {
    setConversationId(id);
    const typingId = addMessage({ role: "assistant", text: "", statusText: "Retrieving history...", isTyping: true });
    try {
      const res = await juriApi.get(`/api/chat/conversations/${id}`);
      const data = res.data;
      const mapped = (data.messages || []).map((m) => ({
        ...m,
        structured: hasStructuredAnalysis(m.metadata?.structured) ? m.metadata.structured : null,
        citations: m.citations || m.metadata?.citations,
        attachments: m.metadata?.attachments || [],
        actions: mapBackendActions(m),
      }));
      setMessages(mapped);
    } catch (e) {
      updateMessage(typingId, { text: "Failed to load conversation.", isTyping: false });
    }
  };

  const deleteConversation = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("Are you sure you want to delete this consultation?")) return;
    try {
      await juriApi.delete(`/api/chat/conversations/${id}`);
      if (conversationId === id) startNewChat();
      fetchConversations();
    } catch (err) {
      console.error("delete failed", err);
    }
  };

  const startNewChat = () => {
    setMessages([]);
    setConversationId(null);
    setAttachedFiles([]);
    setInputValue("");
  };

  const handleSend = async (textOverride) => {
    if (isSending) return;
    const fallbackUploadPrompt =
      attachedFiles.length > 0
        ? "Describe the contents of the uploaded document and summarize the key factual and procedural details."
        : "";
    const textToSend = (textOverride || inputValue || fallbackUploadPrompt).trim();
    if (!textToSend.trim() && attachedFiles.length === 0) return;
    if (!globalUser) {
      addMessage({ role: "assistant", text: "You must be signed in to use Juri Assistant." });
      return;
    }

    setIsSending(true);
    setInputValue("");
    const pendingFiles = attachedFiles;
    setAttachedFiles([]);

    const userMessageId = addMessage({
      role: "user",
      text: textToSend,
      attachments: pendingFiles.map((file) => ({
        filename: file.filename || file.name,
        fileType: file.fileType || file.type,
        viewUrl: file.viewUrl || "",
      })),
    });

    const typingId = addMessage({ role: "assistant", text: "", statusText: "Initializing...", isTyping: true });

    try {
      let uploadedDocuments = [];
      if (pendingFiles.length > 0) {
        updateMessage(typingId, { statusText: `Uploading ${pendingFiles.length} documents...`, isTyping: true });
        const formData = new FormData();
        pendingFiles.forEach((f) => formData.append("files", f.file || f));
        const uploadRes = await juriApi.post("/api/analyze/document", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploadedDocuments = (Array.isArray(uploadRes.data) ? uploadRes.data : []).map((doc) => ({
          documentId: doc.documentId,
          filename: doc.filename || "Uploaded document",
          fileType: doc.fileType || doc.file_type,
          viewUrl: doc.viewUrl || doc.view_url || "",
          analysisJson: doc.analysisJson || doc.analysis_json,
        }));
        if (uploadedDocuments.length > 0) {
          updateMessage(userMessageId, { attachments: uploadedDocuments });
        }
      }

      const authHeaders = await getAuthHeader();
      const response = await fetch(`${JURI_API_BASE}/api/chat/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders,
        },
        body: JSON.stringify({
          query: textToSend,
          conversationId,
          matterContext: {
            court: matterContext.court.trim(),
            caseNumber: matterContext.caseNumber.trim(),
          },
          uploadedDocuments,
        }),
      });

      if (!response.ok) throw new Error(`Stream failed (${response.status})`);
      const reader = response.body?.getReader();
      if (!reader) throw new Error("No reader available");

      const decoder = new TextDecoder();
      let fullText = "";
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          const cleanLine = line.endsWith("\r") ? line.slice(0, -1) : line;

          if (cleanLine.startsWith("STATUS: ")) {
            updateMessage(typingId, { statusText: cleanLine.replace("STATUS: ", ""), isTyping: true });
          } else if (cleanLine.startsWith("SOURCES: ")) {
            try {
              const sources = JSON.parse(cleanLine.replace("SOURCES: ", ""));
              updateMessage(typingId, { citations: sources });
            } catch (e) {}
          } else if (cleanLine.startsWith("CONTENT: ")) {
            const t = cleanLine.replace("CONTENT: ", "");
            fullText += t;
            updateMessage(typingId, { text: fullText, isTyping: true });
          } else if (cleanLine.startsWith("METADATA: ")) {
            try {
              const meta = JSON.parse(cleanLine.replace("METADATA: ", ""));
              if (!conversationId) {
                setConversationId(meta.conversation_id);
                fetchConversations();
              }
              const updates = {
                id: meta.message_id,
                isTyping: false,
                statusText: null,
              };
              // Rich structured sections for legal analysis; natural markdown
              // narrative for plain chat / summaries.
              if (hasStructuredAnalysis(meta.structured)) {
                updates.structured = meta.structured;
                updates.text = "";
              } else {
                updates.structured = null;
                updates.text = fullText;
              }
              updateMessage(typingId, updates);

              // Export/action buttons are intentionally NOT guessed here anymore.
              // The previous hardcoded two-button array didn't know what kind of
              // draft was generated, which is why it was wrong until a refresh
              // pulled the correct, single, backend-persisted action list via
              // loadConversation -> mapBackendActions.
              //
              // Fix: re-fetch that same persisted message right after streaming
              // finishes and run it through the existing mapBackendActions() helper
              // - the exact function that already renders the correct button(s)
              // after a refresh - so the live view and the reloaded view can never
              // disagree, regardless of draft type.
              if (shouldShowActions(meta.structured) && meta.conversation_id && meta.message_id) {
                (async () => {
                  try {
                    const authHeaders = await getAuthHeader();
                    const res = await fetch(
                      `${JURI_API_BASE}/api/chat/conversations/${meta.conversation_id}`,
                      { headers: authHeaders }
                    );
                    if (!res.ok) return;
                    const data = await res.json();
                    const saved = (data.messages || []).find((m) => m.id === meta.message_id);
                    if (!saved) return;
                    const actions = mapBackendActions(saved);
                    if (actions && actions.length) {
                      updateMessage(meta.message_id, { actions });
                    }
                  } catch (e) {
                    // Non-fatal: if this fails, the message simply won't show export
                    // buttons until the conversation is reloaded - same as the
                    // pre-fix behavior, never worse.
                  }
                })();
              }
            } catch (e) {}
          } else if (cleanLine.startsWith("ERROR: ")) {
            throw new Error(cleanLine.replace("ERROR: ", ""));
          } else if (fullText.length > 0) {
            fullText += "\n" + cleanLine;
            updateMessage(typingId, { text: fullText, isTyping: true });
          }
        }
      }
    } catch (err) {
      updateMessage(typingId, {
        text: `### System Notice\nI encountered a technical interruption: **${err.message}**.\n\nPlease verify your connection and try again.`,
        statusText: null,
        isTyping: false,
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const filteredConversations = (Array.isArray(conversations) ? conversations : []).filter((c) =>
    (c.title || "Untitled Case").toLowerCase().includes(searchTerm.toLowerCase())
  );

  const requestRelatedCases = () => {
    handleSend("Show related cases");
  };

  // Recent list is capped until the user taps "View all conversations"; a search
  // term already narrows the list, so show every match in that case.
  const visibleConversations =
    showAllConversations || searchTerm
      ? filteredConversations
      : filteredConversations.slice(0, RECENT_CONVERSATIONS_LIMIT);

  const runSuggestion = (key) => {
    if (key === "issues") return handleSend("Analyze this case and identify the legal issues.");
    if (key === "related") return requestRelatedCases();
    if (key === "brief") return handleSend("Prepare a hearing brief.");
    return setShowTimelineModal(true);
  };

  return (
    <>
      <Navbar />
      <div className="juri-root">
        <div className="juri-layout">
          <aside className={`juri-sidebar ${isSidebarOpen ? "" : "juri-hidden-sidebar"}`}>
            <div className="juri-sidebar-header">
              <div className="juri-sidebar-top-row">
                <div className="juri-brand">
                  Jurident AI <span className="juri-ai-badge">AI</span>
                </div>
                <button
                  className="juri-sidebar-icon-btn"
                  onClick={startNewChat}
                  title="New consultation"
                  aria-label="New consultation"
                >
                  <SquarePen size={18} strokeWidth={1.75} aria-hidden="true" />
                </button>
              </div>
            </div>

            <div className="juri-sidebar-search">
              <Search className="juri-search-icon" size={15} strokeWidth={1.9} aria-hidden="true" />
              <input
                type="text"
                placeholder="Search conversations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="juri-sidebar-label">Recent Conversations</div>

            <div className="juri-conversation-list">
              {visibleConversations.map((c) => (
                <div
                  key={c.id}
                  className={`juri-conv-item ${conversationId === c.id ? "active" : ""}`}
                  onClick={() => loadConversation(c.id)}
                >
                  <span className="juri-conv-icon">
                    <MessageSquare size={15} strokeWidth={1.9} aria-hidden="true" />
                  </span>
                  <div className="juri-conv-info">
                    <div className="juri-conv-title">{c.title || "Untitled Case"}</div>
                    <div className="juri-conv-date">
                      {c.created_at ? new Date(c.created_at).toLocaleDateString("en-GB") : ""}
                    </div>
                  </div>
                  {/* Kebab → single-item menu (Delete), like the reference design. */}
                  <button
                    className="juri-conv-menu-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenConvMenuId((prev) => (prev === c.id ? null : c.id));
                    }}
                    title="More"
                    aria-label={`Options for ${c.title || "conversation"}`}
                  >
                    <MoreVertical size={15} strokeWidth={1.9} aria-hidden="true" />
                  </button>
                  {openConvMenuId === c.id && (
                    <div className="juri-conv-menu" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        onClick={(e) => {
                          setOpenConvMenuId(null);
                          deleteConversation(e, c.id);
                        }}
                      >
                        <Trash2 size={14} strokeWidth={1.9} aria-hidden="true" /> Delete
                      </button>
                    </div>
                  )}
                </div>
              ))}
              {filteredConversations.length === 0 && (
                <div className="juri-conv-empty">No conversations yet.</div>
              )}
            </div>

            {!searchTerm && filteredConversations.length > RECENT_CONVERSATIONS_LIMIT && (
              <button
                type="button"
                className="juri-view-all-convs"
                onClick={() => setShowAllConversations((v) => !v)}
              >
                {showAllConversations ? "Show less" : "View all conversations"}
              </button>
            )}

            {/* Profile footer removed — the portal topbar already shows the
                signed-in user, so the sidebar ends at the conversation list. */}
          </aside>

          <main className="juri-main relative">
            <div className="juri-chat-container">
              <div className="absolute left-4 top-[20px] z-20">
                <button
                  onClick={() => navigate("/portal/Dashboard")}
                  className="juri-back-arrow transition"
                  title="Back to Dashboard"
                >
                  <ArrowLeft size={24} />
                </button>
              </div>
              <header className="juri-chat-header">
                <button
                  className="juri-sidebar-icon-btn juri-open-sidebar"
                  onClick={() => setIsSidebarOpen((open) => !open)}
                  title={isSidebarOpen ? "Close sidebar" : "Open sidebar"}
                  aria-label={isSidebarOpen ? "Close sidebar" : "Open sidebar"}
                >
                  {isSidebarOpen ? (
                    <PanelLeftClose size={18} strokeWidth={1.75} aria-hidden="true" />
                  ) : (
                    <PanelLeftOpen size={18} strokeWidth={1.75} aria-hidden="true" />
                  )}
                </button>
                <div>
                  <h2>Jurident AI</h2>
                </div>
              </header>

              <div className="juri-chat-history">
                {messages.length === 0 && (
                  // Nothing sent yet → centred greeting + suggestion tiles
                  // (the quick-prompt row below the composer is hidden meanwhile).
                  <div className="juri-empty-hero">
                    <h1 className="juri-empty-title">Hello, I&rsquo;m Jurident AI</h1>
                    <p className="juri-empty-sub">
                      I can help you with legal research, case analysis, document review,
                      precedents, and more. How can I assist you today?
                    </p>
                    <div className="juri-empty-cards">
                      {EMPTY_SUGGESTIONS.map(({ key, title, sub, Icon, bg, fg }) => (
                        <button
                          key={key}
                          type="button"
                          className="juri-empty-card"
                          onClick={() => runSuggestion(key)}
                        >
                          <span className="juri-empty-card-icon" style={{ background: bg, color: fg }}>
                            <Icon size={20} strokeWidth={1.9} aria-hidden="true" />
                          </span>
                          <span className="juri-empty-card-title">{title}</span>
                          <span className="juri-empty-card-sub">{sub}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {messages.map((m) => (
                  <div key={m.id} className={`juri-chat-bubble-wrapper ${m.role}`}>
                    <div className={`juri-chat-bubble ${m.role} ${m.structured ? "wide" : ""}`}>
                      {m.isTyping && !m.text ? (
                        <div className="juri-shimmer-container">
                          <div className="juri-shimmer-status">
                            <span className="juri-shimmer-spinner" />
                            {m.statusText || "Analyzing..."}
                          </div>
                          <div className="juri-shimmer-lines">
                            <div className="juri-shimmer-line" style={{ width: "100%" }} />
                            <div className="juri-shimmer-line" style={{ width: "85%" }} />
                            <div className="juri-shimmer-line" style={{ width: "100%" }} />
                            <div className="juri-shimmer-line" style={{ width: "70%" }} />
                            <div className="juri-shimmer-line" style={{ width: "90%" }} />
                            <div className="juri-shimmer-line" style={{ width: "40%" }} />
                          </div>
                        </div>
                      ) : (
                        <div className="juri-text-content">
                          {m.structured ? (
                            <StructuredResponse s={m.structured} onExpandCase={handleExpandCase} expandedCases={expandedCases} />
                          ) : (
                            <SimpleMarkdown text={parseStructuralCases(displayTextWithoutAttachmentLinks(m)) + (m.isTyping ? " ▋" : "")} onExpandCase={handleExpandCase} expandedCases={expandedCases} />
                          )}
                          {m.attachments && m.attachments.length > 0 && (
                            <div className="juri-document-attachments">
                              {m.attachments.map((attachment, idx) => (
                                <DocumentAttachmentCard
                                  key={`${attachment.viewUrl || attachment.filename}-${idx}`}
                                  attachment={attachment}
                                />
                              ))}
                            </div>
                          )}
                          <SourceList citations={m.citations} />
                        </div>
                      )}

                      {!m.isTyping && m.role === "assistant" && (
                        <div className="juri-msg-toolbar">
                          {Boolean(m.text) && (
                            <>
                              <button
                                type="button"
                                className="juri-icon-btn"
                                title={copiedId === m.id ? "Copied" : "Copy"}
                                aria-label="Copy response"
                                onClick={() => copyMessage(m)}
                              >
                                {copiedId === m.id ? <Check size={15} aria-hidden="true" /> : <Copy size={15} aria-hidden="true" />}
                              </button>
                              <button
                                type="button"
                                className="juri-icon-btn"
                                title={playingAudioId === m.id ? "Stop" : "Read aloud"}
                                aria-label="Read response aloud"
                                onClick={() => playAudioOutLoud(m.id, displayTextWithoutAttachmentLinks(m))}
                              >
                                {playingAudioId === m.id ? <Square size={15} aria-hidden="true" /> : <Volume2 size={15} aria-hidden="true" />}
                              </button>
                            </>
                          )}
                          {m.actions && m.actions.map((act, i) => (
                            <button key={i} className="juri-action-btn" onClick={act.onClick}>
                              {act.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              <div className="juri-chat-input-area">
                <div className={`juri-quick-prompts ${messages.length === 0 ? "juri-hidden" : ""}`}>
                  <button onClick={() => handleSend("Analyze this case and identify the legal issues.")}>
                    Identify legal issues
                  </button>
                  <button onClick={requestRelatedCases} title="Ask the assistant to find and explain related cases.">
                    Show Related Cases
                  </button>
                  <button onClick={() => handleSend("Prepare a hearing brief.")}>
                    Prepare hearing brief
                  </button>
                  <button onClick={() => setShowTimelineModal(true)} title="Generate a timeline table of all dates and hearings.">
                    List of hearings
                  </button>
                </div>

                <div className="juri-footer-hint">
                  Jurident AI can make mistakes. Verify important information.
                </div>

                <div className="juri-input-box">
                  {attachedFiles.length > 0 && (
                    <div className="juri-attachments-list">
                      {attachedFiles.map((file, idx) => (
                        <div key={idx} className="juri-attachment-pill">
                          <span>{file.filename || file.name}</span>
                          <button
                            type="button"
                            onClick={() =>
                              setAttachedFiles((prev) => prev.filter((_, i) => i !== idx))
                            }
                            aria-label={`Remove ${file.name}`}
                          >
                            <X size={13} strokeWidth={2} aria-hidden="true" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {isRecording ? (
                    <div className="juri-recording-panel" style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "16px",
                      background: "rgba(239, 68, 68, 0.08)",
                      border: "1px solid rgba(239, 68, 68, 0.3)",
                      borderRadius: "8px",
                      margin: "8px 0"
                    }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        <span className="juri-recording-pulse" style={{
                          width: "12px",
                          height: "12px",
                          backgroundColor: "#ef4444",
                          borderRadius: "50%",
                          display: "inline-block"
                        }}></span>
                        <span style={{ fontSize: "0.9rem", color: "var(--juri-text)", fontWeight: "500" }}>
                          Recording in {LANGUAGES.find(l => l.code === selectedLanguage)?.name}...
                        </span>
                        <span style={{ fontSize: "0.9rem", color: "var(--juri-muted)", fontFamily: "monospace" }}>
                          {formatDuration(recordingDuration)}
                        </span>
                      </div>
                      <div style={{ display: "flex", gap: "8px" }}>
                        <button 
                          type="button"
                          onClick={() => stopRecording(false)} 
                          style={{
                            background: "#22c55e",
                            color: "white",
                            border: "none",
                            padding: "6px 12px",
                            borderRadius: "6px",
                            fontSize: "0.8rem",
                            fontWeight: "bold",
                            cursor: "pointer"
                          }}
                        >
                          Stop & Send
                        </button>
                        <button 
                          type="button"
                          onClick={() => stopRecording(true)} 
                          style={{
                            background: "#ef4444",
                            color: "white",
                            border: "none",
                            padding: "6px 12px",
                            borderRadius: "6px",
                            fontSize: "0.8rem",
                            fontWeight: "bold",
                            cursor: "pointer"
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : isTranscribing ? (
                    <div className="juri-transcribing-panel" style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      padding: "16px",
                      background: "var(--juri-brand-light)",
                      border: "1px solid var(--juri-brand)",
                      borderRadius: "8px",
                      margin: "8px 0"
                    }}>
                      <div className="juri-spinner-loader" style={{
                        width: "16px",
                        height: "16px",
                        border: "2px solid var(--juri-muted)",
                        borderTop: "2px solid var(--juri-brand)",
                        borderRadius: "50%"
                      }}></div>
                      <span style={{ fontSize: "0.9rem", color: "var(--juri-text)" }}>Transcribing audio...</span>
                    </div>
                  ) : (
                  <div className="juri-input-row">
                    <label className="juri-icon-btn juri-attach-btn" title="Upload documents" aria-label="Upload documents">
                      <Plus size={20} strokeWidth={2} aria-hidden="true" />
                      <input
                        type="file"
                        hidden
                        multiple
                        onChange={(e) => {
                          if (e.target.files) {
                            const newFiles = Array.from(e.target.files).map((file) => ({
                              file,
                              filename: file.name,
                              fileType: file.name.split(".").pop()?.toLowerCase() || file.type,
                              viewUrl: URL.createObjectURL(file),
                            }));
                            setAttachedFiles((prev) => [...prev, ...newFiles]);
                            e.currentTarget.value = "";
                          }
                        }}
                      />
                    </label>
                    <textarea
                      rows={1}
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="Ask anything"
                    />
                    <div className="juri-mic-wrap">
                      <button
                        type="button"
                        className="juri-icon-btn juri-mic-btn"
                        data-recording={isRecording ? "true" : "false"}
                        onClick={() => {
                          if (isRecording) {
                            stopRecording(false);
                          } else {
                            setShowLanguages(!showLanguages);
                          }
                        }}
                        title="Speech input"
                        aria-label="Speech input"
                      >
                        <Mic size={18} strokeWidth={2} aria-hidden="true" />
                      </button>
                      
                      {showLanguages && (
                        <div className="juri-language-dropdown" style={{
                          position: "absolute",
                          bottom: "100%",
                          right: 0,
                          marginBottom: "8px",
                          background: "rgba(30, 41, 59, 0.95)",
                          backdropFilter: "blur(12px)",
                          border: "1px solid var(--juri-border)",
                          borderRadius: "12px",
                          padding: "8px",
                          boxShadow: "0 10px 25px -5px rgba(0,0,0,0.5)",
                          zIndex: 1000,
                          minWidth: "180px",
                          display: "flex",
                          flexDirection: "column",
                          gap: "4px",
                          maxHeight: "240px",
                          overflowY: "auto"
                        }}>
                          <div style={{
                            fontSize: "0.75rem",
                            color: "var(--juri-muted)",
                            fontWeight: "600",
                            textTransform: "uppercase",
                            padding: "4px 8px",
                            borderBottom: "1px solid var(--juri-border)",
                            marginBottom: "4px"
                          }}>Select Language</div>
                          {LANGUAGES.map(lang => (
                            <button
                              key={lang.code}
                              type="button"
                              onClick={() => startRecording(lang.code)}
                              style={{
                                background: "transparent",
                                border: "none",
                                color: "var(--juri-text)",
                                padding: "8px 12px",
                                borderRadius: "6px",
                                textAlign: "left",
                                fontSize: "0.85rem",
                                cursor: "pointer",
                                transition: "all 0.1s"
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = "var(--juri-brand-light)";
                                e.currentTarget.style.color = "var(--juri-brand)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = "transparent";
                                e.currentTarget.style.color = "var(--juri-text)";
                              }}
                            >
                              {lang.name}
                            </button>
                          ))}
                        </div>
                      )}
                      
                    </div>
                    <button
                      className="juri-send-btn"
                      onClick={() => handleSend()}
                      title="Send"
                      aria-label="Send message"
                      disabled={isSending || (!inputValue.trim() && attachedFiles.length === 0)}
                    >
                      <ArrowUp size={18} strokeWidth={2.5} aria-hidden="true" />
                    </button>
                  </div>
                  )}
                </div>
              </div>
            </div>
          </main>

          {showTimelineModal && (
            <div className="juri-modal-overlay" style={{
              position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
              background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)",
              display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999
            }}>
              <div className="juri-modal-content" style={{
                background: "var(--juri-bg, #0f172a)", border: "1px solid var(--juri-border, #334155)",
                borderRadius: "12px", width: "90%", maxWidth: "450px", padding: "24px",
                boxShadow: "0 20px 40px rgba(0,0,0,0.4)"
              }}>
                <h3 style={{ margin: "0 0 16px 0", color: "white", fontSize: "1.2rem", fontWeight: "600" }}>Configure Timeline Columns</h3>
                <p style={{ margin: "0 0 16px 0", color: "var(--juri-muted, #94a3b8)", fontSize: "0.9rem", lineHeight: "1.5" }}>
                  Specify the columns you want the AI to extract. You can customize them freely based on your needs.
                </p>
                <input 
                  type="text" 
                  value={timelineColumns} 
                  onChange={(e) => setTimelineColumns(e.target.value)}
                  style={{
                    width: "100%", padding: "12px", borderRadius: "8px",
                    background: "rgba(255,255,255,0.05)", border: "1px solid var(--juri-border, #334155)",
                    color: "white", marginBottom: "24px", boxSizing: "border-box", fontSize: "0.95rem"
                  }}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      setShowTimelineModal(false);
                      if (timelineColumns.trim()) {
                        handleSend(`Extract a tabular chronological timeline of hearings and events. Format it as a Markdown Table with exactly these columns: ${timelineColumns}`);
                      }
                    }
                  }}
                />
                <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}>
                  <button 
                    onClick={() => setShowTimelineModal(false)}
                    style={{
                      background: "transparent", border: "1px solid var(--juri-border, #334155)", color: "white",
                      padding: "8px 16px", borderRadius: "6px", cursor: "pointer", transition: "all 0.2s"
                    }}
                    onMouseEnter={(e) => e.target.style.background = "rgba(255,255,255,0.05)"}
                    onMouseLeave={(e) => e.target.style.background = "transparent"}
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={() => {
                      setShowTimelineModal(false);
                      if (timelineColumns.trim()) {
                        handleSend(`Extract a tabular chronological timeline of hearings and events. Format it as a Markdown Table with exactly these columns: ${timelineColumns}`);
                      }
                    }}
                    style={{
                      background: "var(--juri-brand, #3b82f6)", border: "none", color: "white",
                      padding: "8px 16px", borderRadius: "6px", cursor: "pointer", fontWeight: "bold", transition: "all 0.2s"
                    }}
                    onMouseEnter={(e) => e.target.style.opacity = "0.9"}
                    onMouseLeave={(e) => e.target.style.opacity = "1"}
                  >
                    Generate Table
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default JuriAssistance;
